import Swiper from "./swiper-bundle.esm.browser.min.js";

const BREAKPOINT_TABLET = 600;
const BREAKPOINT_DESKTOP = 900;
const PANEL_TRANSITION_MS = 520;

class CustomAccordionSlider extends HTMLElement {
  constructor() {
    super();
    this._swiperMap = new WeakMap();
    this._observers = [];
    this._pendingTimers = new Set();
    this._initialized = false;
    this._handleKeyboard = this._handleKeyboard.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._init();
    this.addEventListener("keydown", this._handleKeyboard);
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
  }

  disconnectedCallback() {
    this._initialized = false;
    this._clearPendingTimers();
    this._destroyAllSwipers();
    this._disconnectObservers();
    this.removeEventListener("keydown", this._handleKeyboard);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this.reinit();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._clearPendingTimers();
    this._destroyAllSwipers();
    this._disconnectObservers();
  }

  /** Re-initialize accordions and carousels (Theme Editor / programmatic use). */
  reinit() {
    this._clearPendingTimers();
    this._destroyAllSwipers();
    this._disconnectObservers();
    this._init();
  }

  _clearPendingTimers() {
    this._pendingTimers.forEach((id) => clearTimeout(id));
    this._pendingTimers.clear();
  }

  _schedule(fn, delay = 0) {
    const id = setTimeout(() => {
      this._pendingTimers.delete(id);
      fn();
    }, delay);
    this._pendingTimers.add(id);
    return id;
  }

  _init() {
    const panels = this.querySelectorAll("collapsible-section");

    panels.forEach((panel) => {
      this._syncPanelAria(panel);

      if (panel.dataset.open === "true") {
        this._scheduleCarouselInit(panel, { immediate: true });
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName !== "data-open") return;

          this._syncPanelAria(panel);

          if (panel.dataset.open === "true") {
            this._scheduleCarouselInit(panel, { update: true });
          }
        });
      });

      observer.observe(panel, { attributes: true, attributeFilter: ["data-open"] });
      this._observers.push(observer);
    });
  }

  _syncPanelAria(panel) {
    const trigger = panel.querySelector(".wt-collapse__trigger");
    if (!trigger) return;

    const isOpen = panel.dataset.open === "true";
    trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    trigger.classList.toggle("wt-collapse__trigger--active", isOpen);
  }

  _disconnectObservers() {
    this._observers.forEach((observer) => observer.disconnect());
    this._observers = [];
  }

  _scheduleCarouselInit(panel, { update = false, immediate = false } = {}) {
    const delay = immediate ? 0 : PANEL_TRANSITION_MS;

    this._schedule(() => {
      requestAnimationFrame(() => {
        this._initCarouselInPanel(panel, { update });
      });
    }, delay);
  }

  _readCarouselConfig(carousel) {
    const mobile = parseFloat(carousel.dataset.slidesPerViewMobile || "1.1");
    const tablet = parseFloat(carousel.dataset.slidesPerViewTablet || "1.2");
    const desktop = parseFloat(carousel.dataset.slidesPerViewDesktop || "1.4");
    const spaceBetween = parseInt(carousel.dataset.spaceBetween || "8", 10);
    const showArrows = carousel.dataset.showArrows === "true";
    const showPagination = carousel.dataset.showPagination === "true";
    const enableAutoplay = carousel.dataset.enableAutoplay === "true";
    const autoplaySpeed = parseInt(carousel.dataset.autoplaySpeed || "5000", 10);
    const slideCount = parseInt(carousel.dataset.slideCount || "0", 10);
    const navScope = carousel.dataset.navScope || "";

    return {
      mobile,
      tablet,
      desktop,
      spaceBetween,
      showArrows: showArrows && slideCount > 1,
      showPagination: showPagination && slideCount > 1,
      enableLoop: carousel.dataset.enableLoop === "true" && slideCount > 1,
      enableAutoplay: enableAutoplay && slideCount > 1,
      autoplaySpeed,
      pauseOnHover: carousel.dataset.pauseOnHover === "true",
      pauseOnInteraction: carousel.dataset.pauseOnInteraction !== "false",
      slideCount,
      navScope,
    };
  }

  _prepareSwiperStructure(carousel) {
    const swiperEl = carousel.querySelector("[data-swiper]");
    const container = carousel.querySelector("[data-swiper-container]");
    const slideEls = carousel.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) {
      return null;
    }

    swiperEl.classList.add("swiper", "cas-carousel__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { swiperEl, container, slideEls };
  }

  _initCarouselInPanel(panel, { update = false } = {}) {
    if (panel.dataset.open !== "true") return;

    const carousel = panel.querySelector("[data-accordion-carousel]");
    if (!carousel) return;

    const structure = this._prepareSwiperStructure(carousel);
    if (!structure) return;

    const { swiperEl } = structure;
    const existing = this._swiperMap.get(carousel);

    if (existing && !existing.destroyed) {
      if (update) {
        existing.update();
        existing.slideTo(0, 0);
      }
      return;
    }

    if (carousel.dataset.swiperInitialized === "true" && !existing) {
      carousel.removeAttribute("data-swiper-initialized");
    }

    const config = this._readCarouselConfig(carousel);
    const isTextOnlyLayout = carousel.dataset.textOnlyLayout === "true";
    const swiperConfig = {
      grabCursor: true,
      spaceBetween: isTextOnlyLayout ? 0 : config.spaceBetween,
      slidesPerView: isTextOnlyLayout ? 1 : config.mobile,
      watchOverflow: true,
      speed: 280,
      resistanceRatio: 0.85,
      breakpoints: isTextOnlyLayout
        ? {
            [BREAKPOINT_TABLET]: { slidesPerView: 1, spaceBetween: 0 },
            [BREAKPOINT_DESKTOP]: { slidesPerView: 1, spaceBetween: 0 },
          }
        : {
            [BREAKPOINT_TABLET]: {
              slidesPerView: config.tablet,
            },
            [BREAKPOINT_DESKTOP]: {
              slidesPerView: config.desktop,
            },
          },
      on: {
        init: (swiper) => {
          requestAnimationFrame(() => swiper.update());
        },
        resize: (swiper) => {
          swiper.update();
        },
      },
    };

    if (config.enableLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows) {
      const nextEl = carousel.querySelector(
        `.cas-carousel__nav-next--${config.navScope}`,
      );
      const prevEl = carousel.querySelector(
        `.cas-carousel__nav-prev--${config.navScope}`,
      );
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination) {
      const paginationEl = carousel.querySelector(
        `.cas-carousel__pagination--${config.navScope}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const slideNum = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Slide ${slideNum} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay && window.innerWidth >= BREAKPOINT_DESKTOP) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    const swiper = new Swiper(swiperEl, swiperConfig);
    carousel.dataset.swiperInitialized = "true";
    this._swiperMap.set(carousel, swiper);

    requestAnimationFrame(() => {
      swiper.update();
    });
  }

  _destroyAllSwipers() {
    this.querySelectorAll("[data-accordion-carousel]").forEach((carousel) => {
      const swiper = this._swiperMap.get(carousel);
      if (swiper && !swiper.destroyed) {
        swiper.destroy(true, true);
      }
      this._swiperMap.delete(carousel);
      carousel.removeAttribute("data-swiper-initialized");
    });
  }

  _handleKeyboard(event) {
    const key = event.key;
    if (key !== "ArrowLeft" && key !== "ArrowRight") return;

    const focused = document.activeElement;
    if (!this.contains(focused)) return;

    const carousel = focused.closest("[data-accordion-carousel]");
    if (!carousel) return;

    const swiper = this._swiperMap.get(carousel);
    if (!swiper || swiper.destroyed) return;

    event.preventDefault();
    if (key === "ArrowLeft") {
      swiper.slidePrev();
    } else {
      swiper.slideNext();
    }
  }
}

if (!customElements.get("custom-accordion-slider")) {
  customElements.define("custom-accordion-slider", CustomAccordionSlider);
}

/**
 * Initialize or re-initialize an Accordion + Slider instance inside a container.
 * @param {HTMLElement | string} container - Element or selector scoped to one instance
 */
export function initAccordionSlider(container) {
  const root =
    typeof container === "string"
      ? document.querySelector(container)
      : container;

  if (!root) return null;

  const instance = root.matches("custom-accordion-slider")
    ? root
    : root.querySelector("custom-accordion-slider");

  if (!instance) return null;

  if (typeof instance.reinit === "function") {
    instance.reinit();
  }

  return instance;
}
